import './NumericRangeFilter.css';

import { useEffect, useRef, useState } from 'react';
import type { FocusEvent } from 'react';
import {
  FilterType,
  IntegerInputControl,
  RealInputControl,
} from '../../../../../types/types';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';
import { NumberInput } from '../NumberInput/NumberInput';
import type { GridInputBaseProps } from '../../types';

type NumericRangeFilterProps = Omit<GridInputBaseProps, 'isColumnFilter'> & {
  control: IntegerInputControl | RealInputControl;
  type: FilterType;
};

const NumericRangeFilter = ({
  name,
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  error,
  setError,
  disabled,
  control,
  type = 'string',
}: NumericRangeFilterProps) => {
  const { t } = useTranslate();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const isInsideContainer = (node: Node | null) =>
    containerRef.current?.contains(node) ?? false;

  const handleFocusIn = (e?: FocusEvent) => {
    if (!isInsideContainer(e?.relatedTarget as Node | null)) {
      onFocusIn?.(e);
    }
  };

  const handleFocusOut = (e?: FocusEvent) => {
    if (!isInsideContainer(e?.relatedTarget as Node | null)) {
      onFocusOut?.(e);
    }
  };

  // Emit value to both change and touch in the same moment — non-text controls
  // treat `touch` as an undebounced equivalent of `change`. `propagateHandler`
  // defaults to true; pass false for programmatic writes (mount defaults etc.)
  // to skip firing the field-level handlers in FormHandlersContext.
  const emitChange = (v: string, propagateHandler: boolean = true) => {
    onChange(v, propagateHandler);
    if (propagateHandler) {
      onTouch?.(v);
    }
  };

  useEffect(() => {
    if (value) {
      const splitted = String(value).split(',', 2);
      if (splitted[0] && splitted[1]) {
        setFrom(splitted[0]);
        setTo(splitted[1]);
      } else {
        // Invalid value after switching from another matchmode
        emitChange('', false);
        setError(null);
      }
    }
  }, []);

  // NumberInput emits numbers now; keep the range bounds as strings so the CSV
  // build and the truthiness guards (incl. a "0" bound) behave as before.
  const onFromChange = (nextValue: number | string | undefined) => {
    const next = nextValue == null ? '' : String(nextValue);
    setFrom(next);
    setError(null);
    if (!next || !to || parseFloat(next) <= parseFloat(to)) {
      emitChange(`${next},${to}`);
    }
  };

  const onToChange = (nextValue: number | string | undefined) => {
    const next = nextValue == null ? '' : String(nextValue);
    setTo(next);
    setError(null);
    if (!next || !from || parseFloat(next) >= parseFloat(from)) {
      emitChange(`${from},${next}`);
    }
  };

  const onBlur = () => {
    if (from && to) {
      const fromNumber = parseFloat(from);
      const toNumber = parseFloat(to);
      if (fromNumber > toNumber) {
        setError([t('errors.invalidBetweenError')]);
      }
    }
  };

  return (
    <>
      <div
        className="numeric-range-filter range-filter-container"
        ref={containerRef}
      >
        <NumberInput
          name={name}
          value={from}
          onChange={onFromChange}
          onFocusIn={handleFocusIn}
          onFocusOut={handleFocusOut}
          disabled={disabled}
          error={error}
          control={control}
          isFloat={type === 'float'}
          isFormInput={false}
          setError={setError}
          onBlur={onBlur}
          hideErrorText
        />
        <span className="range-dash">-</span>
        <NumberInput
          name={name}
          value={to}
          onChange={onToChange}
          onFocusIn={handleFocusIn}
          onFocusOut={handleFocusOut}
          disabled={disabled}
          error={error}
          control={control}
          isFloat={type === 'float'}
          isFormInput={false}
          setError={setError}
          onBlur={onBlur}
          hideErrorText
        />
      </div>
      <InputErrorMessage error={error} className="range-error" />
    </>
  );
};

export default NumericRangeFilter;
